import { useState } from "react"

import { usePointerPosition } from "shared/lib/drag-and-drop/usePointerPosition"
import { useThrottle } from "shared/lib/performance/useThrottle"
import { useEvent } from "shared/lib/useEvent"
import { isDragSource } from "shared/types/drag-and-drop/DragSource"
import { isDropTarget } from "shared/types/drag-and-drop/DropTarget"

import { DragMoveEvent, useDndMonitor } from "@dnd-kit/core"

import { FollowUpConfigurationDragMetaSchema } from "../types/FollowUpConfigurationDragMeta"

export interface FollowUpConfigurationDropHint {
  entryID: string
  insertBefore: boolean
}

interface UseFollowUpConfigurationReorderDnDProps {
  groupID: string
  onReorder: (
    entryID: string,
    targetEntryID: string,
    insertBefore: boolean
  ) => void
}

/**
 * Tracks the row the pointer currently hovers within this group so it can
 * render an insertion indicator, and turns the drop into a reorder of two
 * neighbours. Has to be called inside the `DnDProvider` of the page. Drags
 * originating from a different group are ignored since rows may only be
 * reordered inside their own source product.
 */
export function useFollowUpConfigurationReorderDnD({
  groupID,
  onReorder
}: UseFollowUpConfigurationReorderDnDProps) {
  const { getPointerPosition } = usePointerPosition()
  const [dropHint, setDropHint] =
    useState<FollowUpConfigurationDropHint | null>(null)

  const handleDragMove = useEvent((event: DragMoveEvent) => {
    if (
      !isDropTarget(event.over?.data.current, {
        metaSchema: FollowUpConfigurationDragMetaSchema
      }) ||
      event.over.data.current.meta.groupID !== groupID
    ) {
      setDropHint(null)
      return
    }

    const pointerPosition = getPointerPosition()
    if (!pointerPosition) return

    const { entryID } = event.over.data.current.meta
    setDropHint({
      entryID,
      insertBefore:
        pointerPosition.y < event.over.rect.top + event.over.rect.height / 2
    })
  })

  const throttledDragMove = useThrottle(handleDragMove)

  useDndMonitor({
    onDragMove: throttledDragMove,
    onDragEnd: event => {
      setDropHint(null)

      if (
        !dropHint ||
        !isDragSource(event.active.data.current, {
          metaSchema: FollowUpConfigurationDragMetaSchema
        })
      ) {
        return
      }

      const { entryID, groupID: sourceGroupID } = event.active.data.current.meta
      if (entryID === dropHint.entryID || sourceGroupID !== groupID) return

      onReorder(entryID, dropHint.entryID, dropHint.insertBefore)
    }
  })

  return dropHint
}
